import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { StatusBadge, SyncArrow, ErpBadge } from "../components/Badges";
import StatusPill from "../components/StatusPill";
import PaymentPill from "../components/PaymentPill";
import InvoiceNotes from "../components/InvoiceNotes";
import { useAuth } from "../lib/auth";
import { formatCurrency, formatDate, formatDateTime, erpLabel } from "../lib/format";
import { API_BASE } from "../lib/api";
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, Clock, Printer, Download } from "lucide-react";

function Field({ label, children, mono, testid }) {
  return (
    <div className="py-3 border-b border-neutral-200 flex items-baseline justify-between gap-6" data-testid={testid}>
      <div className="label-mini">{label}</div>
      <div className={mono ? "font-mono text-sm text-right" : "text-sm text-right"}>{children}</div>
    </div>
  );
}

const money = (v, cur) =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? "—" : formatCurrency(Number(v), cur);

const HELD_STATUSES = ["needs_mapping", "awaiting_sender_approval", "sender_blocked", "no_match", "held_billing"];

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setData(data);
    } catch (e) {
      setError("Invoice not found");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Prompt 22b: show the receiver's Tax account mapping next to the tax total.
  const [taxAcct, setTaxAcct] = useState(null);
  const taxInv = data?.invoice || data;
  const isReceiver = taxInv && user?.company_id && taxInv.receiver_company_id === user.company_id;
  useEffect(() => {
    if (!isReceiver || !(Number(taxInv?.tax_amount) > 0) || !taxInv?.target_erp) return;
    api.get("/mappings/tax", { params: { erp: taxInv.target_erp } })
      .then(({ data: t }) => setTaxAcct(t.needs_tax_line ? (t.account || { missing: true }) : null))
      .catch(() => setTaxAcct(null));
  }, [isReceiver, taxInv?.tax_amount, taxInv?.target_erp]);

  const onSync = async () => {
    setSyncing(true);
    try {
      await api.post(`/invoices/${id}/sync`);
      await load();
    } finally {
      setSyncing(false);
    }
  };

  if (error) {
    return (
      <Layout>
        <div className="p-8" data-testid="invoice-not-found">{error}</div>
      </Layout>
    );
  }
  if (!data) {
    return (
      <Layout>
        <div className="p-8 label-mini" data-testid="invoice-loading">loading…</div>
      </Layout>
    );
  }

  const inv = data.invoice;
  const events = data.events || [];

  return (
    <Layout>
      <PageHeader
        eyebrow="invoice detail"
        title={inv.invoice_number}
        description={`${inv.customer_name} · ${money(inv.total_amount, inv.currency)}`}
        testid="invoice-detail-header"
        help="invoice-statuses"
        actions={
          <>
            <button
              type="button"
              className="btn-outline flex items-center gap-2"
              onClick={() => navigate("/invoices")}
              data-testid="back-to-invoices"
            >
              <ArrowLeft size={12} /> back
            </button>
            <button
              type="button"
              className="btn-outline flex items-center gap-2"
              onClick={() => window.print()}
              data-testid="print-invoice"
            >
              <Printer size={12} /> print / pdf
            </button>
            <a
              className="btn-outline flex items-center gap-2"
              href={`${API_BASE}/invoices/${inv.id}/export.csv`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-invoice-csv"
            >
              <Download size={12} /> csv
            </a>
            {inv.sync_status !== "no_match" && (
              <button
                type="button"
                className="btn-primary flex items-center gap-2"
                onClick={onSync}
                disabled={syncing}
                data-testid="manual-sync-invoice"
              >
                <RefreshCw size={14} className={syncing ? "pulse" : ""} />
                {syncing ? "syncing…" : "sync now"}
              </button>
            )}
          </>
        }
      />

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fields panel */}
        <div className="lg:col-span-2 card-flat p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="label-mini">10 erp fields</div>
              <h3 className="font-display text-xl font-semibold">Invoice Record</h3>
            </div>
            <div className="flex items-center gap-2">
              {/* Prompt 09: single source of truth — the pipe sync badge.
                  Removed the duplicate StatusBadge(inv.status) that showed
                  the AR status (pending/overdue) and conflicted with the
                  Held/Ready/Synced/Failed badge next to it. */}
              <StatusPill invoice={inv} withHelp />
              {(HELD_STATUSES.includes(inv.sync_status) || String(inv.held_reason || "").startsWith("Held")) && (
                <Link to="/help#fixing-held-invoices" className="text-xs underline whitespace-nowrap"
                      data-testid="how-to-fix-held">How to fix</Link>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 mt-4">
            <Field label="invoice #" mono testid="field-invoice-number">{inv.invoice_number}</Field>
            <Field label="status" testid="field-status"><StatusPill invoice={inv} /></Field>
            <Field label="payment" testid="field-payment">
              <div className="flex items-center gap-2 justify-end">
                <PaymentPill invoice={inv} />
                {typeof inv.amount_due === "number" && inv.amount_due > 0 && (
                  <span className="font-mono text-xs text-neutral-500">
                    {money(inv.amount_due, inv.currency)} due
                  </span>
                )}
              </div>
            </Field>
            <Field label="vendor" testid="field-vendor">
              <span className="font-display font-semibold">{inv.sender_company_name || "—"}</span>
            </Field>
            <Field label="customer" testid="field-customer">{inv.customer_name}</Field>
            <Field label="customer email" testid="field-email">{inv.customer_email}</Field>
            <Field label="company" testid="field-receiver">
              {inv.receiver_company_name || <span className="italic text-neutral-500">unmatched</span>}
            </Field>
            <Field label="issue date" mono testid="field-issue-date">{formatDate(inv.issue_date)}</Field>
            <Field label="due date" mono testid="field-due-date">{formatDate(inv.due_date)}</Field>
            <Field label="total amount" mono testid="field-total">{money(inv.total_amount, inv.currency)}</Field>
            <Field label="tax amount" mono testid="field-tax">
              {money(inv.tax_amount, inv.currency)}
              {taxAcct && (
                <span className="block text-xs text-neutral-500 font-sans" data-testid="field-tax-account">
                  {taxAcct.missing ? "→ tax account not mapped" : `→ ${taxAcct.account_name || taxAcct.account_id}`}
                </span>
              )}
            </Field>
            <Field label="currency" mono testid="field-currency">{inv.currency}</Field>
            <Field label="note to customer" testid="field-description">{inv.description}</Field>
          </div>

          <div className="border-t border-neutral-900 mt-6 pt-4">
            <div className="label-mini">sync metadata</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 mt-2">
              <Field label="source erp" testid="field-source"><ErpBadge erp={inv.source_erp} /></Field>
              <Field label="target erp" testid="field-target"><ErpBadge erp={inv.target_erp} /></Field>
              <Field label="flow" testid="field-flow"><SyncArrow from={inv.source_erp} to={inv.target_erp} /></Field>
              <Field label="last sync" mono testid="field-last-sync">
                {inv.last_sync_at ? formatDateTime(inv.last_sync_at) : "never"}
              </Field>
              {inv.paid_at && (
                <Field label="paid at" mono testid="field-paid-at">
                  <span className="text-green-700">
                    {formatDateTime(inv.paid_at)}
                    {inv.paid_source && (
                      <span className="text-neutral-500 ml-2">
                        · {inv.paid_source.replace(/_/g, " ")}
                      </span>
                    )}
                  </span>
                </Field>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className="border-t border-neutral-900 mt-6 pt-4" data-testid="line-items-section">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-mini">line items</div>
                <h4 className="font-display text-lg font-semibold">
                  {inv.line_items?.length || 0} line{(inv.line_items?.length || 0) === 1 ? "" : "s"}
                </h4>
              </div>
              <div className="label-mini">
                subtotal · <span className="text-neutral-900 font-mono">
                  {money(
                    inv.total_amount == null ? null : inv.total_amount - (Number(inv.tax_amount) || 0),
                    inv.currency
                  )}
                </span>
              </div>
            </div>
            {(!inv.line_items || inv.line_items.length === 0) ? (
              <div className="text-xs text-neutral-500 mt-4 italic">
                No line item detail on this invoice (legacy or single-line invoice).
              </div>
            ) : (
              <div className="mt-3 -mx-6 overflow-x-auto">
                <table className="data-table" data-testid="line-items-table">
                  <thead>
                    <tr>
                      <th>sku</th>
                      <th>description</th>
                      <th className="text-right">qty</th>
                      <th className="text-right">unit price</th>
                      <th className="text-right">amount</th>
                      <th>gl account</th>
                      <th className="text-right">action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inv.line_items.map((li, idx) => (
                      <tr key={`${inv.id}-line-${idx}-${li.description}`} data-testid={`line-item-${idx}`}>
                        <td className="font-mono text-xs" data-testid={`line-sku-${idx}`}>{li.sku || "—"}</td>
                        <td>{li.description}</td>
                        <td className="text-right font-mono">{Number(li.quantity || 0).toLocaleString()}</td>
                        <td className="text-right font-mono">{money(li.unit_price, inv.currency)}</td>
                        <td className="text-right font-mono">{money(li.amount, inv.currency)}</td>
                        <td>
                          {li.target_gl_account_name ? (
                            <div>
                              <div className="text-sm">{li.target_gl_account_name}</div>
                              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                                {li.mapping_status}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-red-600 uppercase tracking-widest">unmapped</span>
                          )}
                        </td>
                        <td className="text-right">
                          {inv.receiver_company_id ? (
                            <button
                              type="button"
                              className="text-xs underline"
                              onClick={() =>
                                navigate(
                                  `/mappings?status=${li.mapping_status === "mapped" ? "mapped" : "unmatched"}&q=${encodeURIComponent(li.source_item_key || li.description || "")}`
                                )
                              }
                              data-testid={`line-map-${idx}`}
                            >
                              {li.mapping_status === "mapped" ? "edit" : "map"}
                            </button>
                          ) : (
                            <span className="text-xs text-neutral-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={4} className="text-right label-mini">tax</td>
                      <td className="text-right font-mono">{money(inv.tax_amount, inv.currency)}</td>
                      <td colSpan={2}></td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="text-right label-mini font-display text-base text-neutral-900">total</td>
                      <td className="text-right font-mono font-semibold">{money(inv.total_amount, inv.currency)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="space-y-6">
          <InvoiceNotes invoiceId={inv.id} currentUser={user} />

          <div className="card-flat">
            <div className="px-5 py-4 border-b border-neutral-900">
              <div className="label-mini">audit trail</div>
              <h3 className="font-display text-xl font-semibold">Sync History</h3>
            </div>
            <div className="px-5 py-2" data-testid="sync-history">
              {events.length === 0 && (
                <div className="text-xs text-neutral-500 py-4">no sync events yet.</div>
              )}
              <ol className="relative border-l border-neutral-300 ml-2 mt-3">
                {events.map((ev) => (
                  <li key={ev.id} className="mb-6 ml-4" data-testid={`history-${ev.id}`}>
                    <span className="absolute -left-[7px] flex items-center justify-center w-3 h-3 bg-white border border-neutral-900">
                      {ev.status === "synced" && <CheckCircle2 size={10} className="text-green-600" />}
                      {ev.status === "failed" && <AlertTriangle size={10} className="text-red-600" />}
                      {ev.status === "pending" && <Clock size={10} className="text-amber-600" />}
                    </span>
                    <div className="text-xs uppercase tracking-widest text-neutral-500">
                      {ev.status} · {erpLabel(ev.source_erp)} → {erpLabel(ev.target_erp)}
                    </div>
                    <div className="text-xs text-neutral-800 mt-1 break-words">{ev.message}</div>
                    <div className="label-mini mt-1">{formatDateTime(ev.created_at)} · {ev.triggered_by}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Printable layout — hidden on screen, shown only in print.
          See the @media print block in index.css. */}
      <PrintableInvoice invoice={inv} />
    </Layout>
  );
}

function PrintableInvoice({ invoice }) {
  const li = invoice.line_items || [];
  const subtotal = invoice.total_amount == null ? null : invoice.total_amount - (Number(invoice.tax_amount) || 0);
  const paymentLabel = (() => {
    const ps = invoice.sync_status === "synced" ? invoice.payment_status : "not_in_gl";
    return ({ paid: "Paid", partial: "Partial", unpaid: "Unpaid",
             not_in_gl: "Not in GL yet" }[ps] || "Unknown");
  })();
  // Prompt 09: pipe status label (Held / Ready / Synced / Failed) — same
  // source as the sync badge, so print matches screen.
  const statusLabel = (() => {
    const map = {
      synced: "Synced", failed: "Failed", pending: "Ready",
      needs_mapping: "Held", awaiting_sender_approval: "Held",
      sender_blocked: "Held", no_match: "Held", held_billing: "Held",
    };
    return map[(invoice.sync_status || "").toLowerCase()] || "—";
  })();
  return (
    <div className="invoice-print" data-testid="invoice-print-layout">
      <header className="print-header">
        <div>
          <div className="print-eyebrow">Lingua Franca</div>
          <h1 className="print-title">Invoice {invoice.invoice_number}</h1>
        </div>
        <div className="print-meta">
          <div><span>Issued</span> {invoice.issue_date || "—"}</div>
          <div><span>Due</span> {invoice.due_date || "—"}</div>
          <div><span>Status</span> {statusLabel}</div>
          <div><span>Payment</span> {paymentLabel}</div>
        </div>
      </header>

      <section className="print-parties">
        <div>
          <div className="print-eyebrow">Vendor</div>
          <div className="print-strong">{invoice.sender_company_name || "—"}</div>
        </div>
        <div>
          <div className="print-eyebrow">Customer</div>
          <div className="print-strong">{invoice.customer_name || "—"}</div>
          <div className="print-sub">{invoice.customer_email || "—"}</div>
        </div>
      </section>

      <table className="print-lines">
        <thead>
          <tr>
            <th>SKU</th><th>Description</th>
            <th className="num">Qty</th><th className="num">Unit price</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {li.length === 0 ? (
            <tr><td colSpan={5} className="print-empty">No line items recorded.</td></tr>
          ) : li.map((l, i) => (
            <tr key={i}>
              <td data-testid={`print-line-sku-${i}`}>{l.sku || "—"}</td>
              <td>{l.description || "—"}</td>
              <td className="num">{l.quantity ?? "—"}</td>
              <td className="num">{money(l.unit_price, invoice.currency)}</td>
              <td className="num">{money(l.amount, invoice.currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={3} /><th className="num">Subtotal</th><td className="num">{money(subtotal, invoice.currency)}</td></tr>
          {invoice.tax_amount != null && (
            <tr><td colSpan={3} /><th className="num">Tax</th><td className="num">{money(invoice.tax_amount, invoice.currency)}</td></tr>
          )}
          <tr><td colSpan={3} /><th className="num total">Total</th><td className="num total">{money(invoice.total_amount, invoice.currency)}</td></tr>
        </tfoot>
      </table>

      <footer className="print-footer">
        <div>LF id · {invoice.id}</div>
        <div>Source GL · {invoice.source_erp} → Target GL · {invoice.target_erp || "—"}</div>
      </footer>
    </div>
  );
}
